import React from 'react';
import TimeSeriesChart from './TimeSeriesChart';

const AIStorageMarketChart = ({ dramDataset, ssdDataset, isZh = false }) => {
    const dramRows = dramDataset?.series || [];
    const ssdRows = ssdDataset?.series || [];
    const roundedVendorTotal = ssdRows.reduce((sum, row) => sum + Number(row.value || 0), 0);
    const maxVendorRevenue = Math.max(...ssdRows.map((row) => row.value), 1);
    const firstSpot = dramRows[0]?.value;
    const latestSpot = dramDataset?.latestSpotPrice;
    const spotChange = Number.isFinite(firstSpot) && Number.isFinite(latestSpot)
        ? ((latestSpot / firstSpot) - 1) * 100
        : null;
    const ddr5Contract = dramDataset?.contractQuotes?.find((quote) => quote.product === 'DDR5 8GB SO-DIMM');

    if (!dramRows.length || !ssdRows.length) {
        return <div className="ai-dev-chart-empty">Storage market data unavailable</div>;
    }

    return (
        <div className="ai-dev-storage-chart">
            <div className="ai-dev-storage-chart__pulse" aria-label={isZh ? '内存与存储市场关键指标' : 'Memory and storage market key metrics'}>
                <span>
                    <small>{isZh ? 'DDR5 16Gb 现货均价' : 'DDR5 16Gb spot average'}</small>
                    <strong>${latestSpot.toFixed(2)}</strong>
                    <em>{dramDataset.latestSpotDate}</em>
                </span>
                <span>
                    <small>{isZh ? '较序列起点' : 'Since series start'}</small>
                    <strong>+{spotChange.toFixed(0)}%</strong>
                    <em>{dramDataset.coverageStart}</em>
                </span>
                <span>
                    <small>{isZh ? '企业级 SSD 行业收入' : 'Enterprise SSD revenue'}</small>
                    <strong>${ssdDataset.reportedTotal.toFixed(2)}B</strong>
                    <em>1Q26 · +{ssdDataset.quarterGrowth.toFixed(1)}% QoQ</em>
                </span>
                <span>
                    <small>{isZh ? '企业级 SSD 合同价环比' : 'Enterprise SSD contract price QoQ'}</small>
                    <strong>≈+{ssdDataset.contractPriceGrowth}%</strong>
                    <em>1Q26</em>
                </span>
            </div>

            <div className="ai-dev-storage-chart__grid">
                <section className="ai-dev-storage-chart__pane">
                    <header>
                        <div>
                            <span>{isZh ? 'DRAM · 市场价格' : 'DRAM · MARKET PRICE'}</span>
                            <h4>{isZh ? 'DDR5 16Gb 芯片现货价格趋势' : 'DDR5 16Gb chip spot-price trend'}</h4>
                        </div>
                        <small>{isZh ? '美元 / 颗' : 'USD / chip'}</small>
                    </header>
                    <div className="ai-dev-storage-chart__dram">
                        <TimeSeriesChart
                            dataset={dramDataset}
                            range="All"
                            ariaLabel={isZh ? 'DDR5 16Gb DRAM 芯片现货价格趋势' : 'DDR5 16Gb DRAM chip spot-price trend'}
                            valueFormatter={(value) => `$${value.toFixed(2)}`}
                            zeroBaseline
                        />
                    </div>
                    {ddr5Contract ? (
                        <div className="ai-dev-storage-chart__contract">
                            <div>
                                <span>{isZh ? '最新公开合约报价' : 'Latest public contract quote'}</span>
                                <strong>${ddr5Contract.value.toFixed(0)}</strong>
                            </div>
                            <p>
                                <strong>{ddr5Contract.product}</strong>
                                <span>{dramDataset.contractPriceDate} · {isZh ? '美元 / 模组' : 'USD / module'}</span>
                            </p>
                        </div>
                    ) : null}
                    <p>{isZh ? 'DRAM 没有标准化交易所期货。TrendForce 的连续公开序列是现货价；合约价按另一产品规格单列，不能与单颗芯片现货价直接比较。' : 'DRAM has no standardized exchange-traded futures. TrendForce’s continuous public series is spot pricing; the contract quote uses a different product and is shown separately.'}</p>
                </section>

                <section className="ai-dev-storage-chart__pane">
                    <header>
                        <div>
                            <span>{isZh ? '企业级 SSD · 市场结构' : 'ENTERPRISE SSD · MARKET MIX'}</span>
                            <h4>{isZh ? '2026 年一季度头部厂商收入' : 'Leading-vendor revenue in 1Q26'}</h4>
                        </div>
                        <small>{isZh ? '十亿美元 / 季度' : 'USD billions / quarter'}</small>
                    </header>
                    <div className="ai-dev-storage-chart__stack" role="img" aria-label={isZh ? '2026 年一季度企业级 SSD 厂商收入占比' : 'Enterprise SSD vendor revenue mix in 1Q26'}>
                        {ssdRows.map((row) => (
                            <i
                                key={row.vendor}
                                style={{ width: `${(row.value / roundedVendorTotal) * 100}%`, background: row.color }}
                                title={`${row.vendor}: $${row.value.toFixed(2)}B`}
                            />
                        ))}
                    </div>
                    <div className="ai-dev-storage-chart__vendors">
                        {ssdRows.map((row) => {
                            const share = row.value / roundedVendorTotal;
                            return (
                                <div className="ai-dev-storage-chart__vendor" key={row.vendor}>
                                    <div>
                                        <span><i style={{ background: row.color }} />{row.vendor}</span>
                                        <strong>${row.value.toFixed(2)}B <small>{(share * 100).toFixed(1)}%</small></strong>
                                    </div>
                                    <div className="ai-dev-storage-chart__track">
                                        <i style={{ width: `${(row.value / maxVendorRevenue) * 100}%`, background: row.color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p>{isZh ? `头部五家公布值合计 $${roundedVendorTotal.toFixed(2)}B；因单家公司数字取整，与 TrendForce 行业总额 $${ssdDataset.reportedTotal.toFixed(2)}B 相差 $0.01B。` : `Rounded vendor figures sum to $${roundedVendorTotal.toFixed(2)}B, $0.01B above TrendForce's $${ssdDataset.reportedTotal.toFixed(2)}B industry headline.`}</p>
                </section>
            </div>
        </div>
    );
};

export default AIStorageMarketChart;
